import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTh, faThumbtack } from "@fortawesome/free-solid-svg-icons";
import { PrismButton, PrismLoading } from "@prism-ui/react";
import { getPastProjectFairs } from "../../services/projectFairDetails.js";
import PageHeader from "../PageHeader";
import PermissionLevel from "../../data/PermissionLevel";
import ProjectCard from "./ProjectCard";
import { ViewToggle } from "./ViewToggle";
import { FilterBar } from "./FilterBar";
import { SortControl } from "./SortControl";
import { applyPipeline } from "./projects.utils";
import type { Project, SortMode, ViewMode } from "./projects.utils";
import { useDebouncedValue } from "./useDebouncedValue";
import { useProgressiveList } from "./useProgressiveList";
import "./Projects.css";

const TagCloudView = lazy(() => import("./TagCloudView"));
const TimelineView = lazy(() => import("./TimelineView"));

const VALID_VIEWS: ViewMode[] = ["classic", "compact", "tagcloud", "timeline"];

interface ProjectsProps {
  currentUser?: {
    user_id?: number | string;
    Role?: { permission_level: number };
    User?: { projects_view?: ViewMode };
  };
  /** Refetches the App-level currentUser so a saved default survives SPA navigation. */
  refreshCurrentUser?: () => Promise<unknown> | void;
}

const Projects = ({ currentUser, refreshCurrentUser }: ProjectsProps) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [seed] = useState(() => Date.now());

  const initialView = currentUser?.User?.projects_view;
  const initialSaved =
    initialView && VALID_VIEWS.includes(initialView) ? initialView : undefined;
  const [viewMode, setViewMode] = useState<ViewMode>(initialSaved ?? "compact");
  // The view the user has explicitly saved as their default (undefined = never set,
  // so the app-wide default of "compact" applies). Tab clicks are ephemeral and do NOT
  // change this — only the "Set as default" pin does.
  const [savedView, setSavedView] = useState<ViewMode | undefined>(initialSaved);

  const [selectedYears, setSelectedYears] = useState<Array<number | string>>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortMode>("random");

  const isOfficer = (currentUser?.Role?.permission_level ?? 0) >= PermissionLevel.Officer;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data: Project[] = await getPastProjectFairs();
        if (!cancelled) setProjects(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load past project fairs:", error);
        if (!cancelled) setProjects([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const availableYears = useMemo(
    () => [...new Set(projects.map((p) => p.year))].sort((a, b) => Number(b) - Number(a)),
    [projects]
  );
  const availableTags = useMemo(
    () => [...new Set(projects.flatMap((p) => (p.tags ?? []).map((t) => t.tag.name)))].sort(),
    [projects]
  );
  const availableLocations = useMemo(
    () => [...new Set(projects.map((p) => p.Chapter?.location).filter(Boolean) as string[])].sort(),
    [projects]
  );

  const debouncedSearch = useDebouncedValue(search, 200);

  // Debounce the facet selections that feed the heavy pipeline + card grid, the same way
  // search is debounced. Toggling a Year/Topic/Location checkbox updates the raw selection
  // state instantly — so the checkbox and its chip react immediately — but applyPipeline
  // and the ~500-card re-mount only run once the user pauses (250 ms). Without this, every
  // click re-filtered and re-mounted hundreds of Prism web components synchronously, so
  // rapid clicks stacked up unmount/remount churn and the UI fell behind.
  const debouncedYears = useDebouncedValue(selectedYears, 250);
  const debouncedTags = useDebouncedValue(selectedTags, 250);
  const debouncedLocations = useDebouncedValue(selectedLocations, 250);

  const filtered = useMemo(
    () =>
      applyPipeline(projects, {
        years: debouncedYears,
        tags: debouncedTags,
        locations: debouncedLocations,
        search: debouncedSearch,
        sort,
        seed,
      }),
    [projects, debouncedYears, debouncedTags, debouncedLocations, debouncedSearch, sort, seed]
  );

  // The filter bar only makes sense in the list views (Classic/Compact), where its output
  // is a readable, filtered card grid. In the visualization views (Tag Cloud, Timeline) it
  // can only express itself as highlight/dim over data you can't actually read here, so it
  // is hidden — those views are pure explorers and hand off to Classic (via a node/year
  // click) where the real filter bar and list live. Filter state is preserved across the
  // switch, just not shown.
  const showFilterBar = viewMode === "classic" || viewMode === "compact";

  // A filter/search change is "in flight" during the debounce window: the live selection
  // has changed but the debounced value the heavy pipeline reads hasn't caught up yet.
  // useDebouncedValue returns the exact value it settled on, so once settled the debounced
  // ref equals the live ref and this is false; it is only true while a change is pending.
  const isFiltering =
    selectedYears !== debouncedYears ||
    selectedTags !== debouncedTags ||
    selectedLocations !== debouncedLocations ||
    search !== debouncedSearch;

  // Progressive rendering: paint an initial batch immediately, then mount the rest over
  // subsequent frames so the full list appears quickly without one blocking commit.
  //
  // The reset key is `showFilterBar` (true only in the Classic/Compact list views). This
  // deliberately does NOT change when toggling Classic<->Compact — they render the same
  // card set, so that flip should only change each card's `collapsed` prop, never remount.
  // It DOES change when returning to a list view from Tag Cloud/Timeline: while a
  // visualization is open the card list is unmounted, but this hook keeps running and its
  // count grows to the full length in the background. Without the reset, switching back
  // would mount every card in one blocking commit (the slow "loading" the user sees).
  // Resetting to the initial batch on re-entry re-spreads that mount cost across frames.
  const { visible } = useProgressiveList(filtered, { resetKey: showFilterBar });

  // Full-view processing splash shown the instant a Tag Cloud / Timeline cross-filter is
  // clicked, covering the wait *before* Classic paints — rather than a cue that only appears
  // once results are already on screen (which the user rightly called "the wrong side of the
  // action"). It is set by the cross-filter handlers and cleared once the debounced selection
  // has caught up (isFiltering settles), so Classic renders the final result set directly
  // instead of first mounting the whole pre-filter set and then narrowing. Because the splash
  // replaces the content while pending, the heavy Classic mount is deferred until the splash
  // is already on screen. Once cleared, the remaining cards mount progressively in the
  // background — the user has results to look at, so that trailing paint needs no cue. Normal
  // in-view typing/faceting never triggers this splash.
  const [crossFilterPending, setCrossFilterPending] = useState(false);
  useEffect(() => {
    if (crossFilterPending && !isFiltering) setCrossFilterPending(false);
  }, [crossFilterPending, isFiltering]);

  // Persist the *currently active* view as this user's default. This is the only path
  // that writes projects_view — tab switching no longer does it implicitly. After the
  // save, refresh the App-level currentUser so the new default survives navigating away
  // and back (currentUser is fetched once at app load and cached in App state).
  const saveDefaultView = () => {
    setSavedView(viewMode);
    if (currentUser?.user_id) {
      axios
        .patch(`/api/users/${currentUser.user_id}/projects_view`, { projects_view: viewMode })
        .then(() => refreshCurrentUser?.())
        .catch((err) => console.error("Failed to persist projects_view:", err));
    }
  };

  const handleTagCrossFilter = (tag: string) => {
    setSelectedTags([tag]);
    setCrossFilterPending(true);
    setViewMode("classic");
  };
  const handleTagsCrossFilter = (tags: string[]) => {
    setSelectedTags(tags);
    setCrossFilterPending(true);
    setViewMode("classic");
  };
  const handleYearCrossFilter = (year: number | string) => {
    setSelectedYears([year]);
    setCrossFilterPending(true);
    setViewMode("classic");
  };

  const cardList = (collapsed: boolean) =>
    filtered.length > 0 ? (
      <ul className="project-list">
        {visible.map((p) => (
          <li key={p.id} className="project">
            <ProjectCard data={p} editable={isOfficer} collapsed={collapsed} />
          </li>
        ))}
      </ul>
    ) : (
      <div className="project-fair-text clr:text-center">
        <p>
          <FontAwesomeIcon icon={faTh} size="6x" className="clr:text-secondary-base text-faded-25" />
        </p>
        <p className="clr:text-secondary-base">No Results Found!</p>
      </div>
    );

  return (
    <>
      <PageHeader title="Projects" />
      <main
        className={
          viewMode === "tagcloud" || viewMode === "timeline"
            ? "projects projects--fit"
            : "projects"
        }
      >
        <div className="project-fair-controls">
          <div className="projects-view-toggle-row">
            <ViewToggle value={viewMode} onChange={setViewMode} />
            <PrismButton
              className="projects-default-pin"
              tone={viewMode === savedView ? "primary-brand" : "secondary"}
              display={viewMode === savedView ? "fill" : "ghost"}
              size="sm"
              disabled={viewMode === savedView}
              setAriaLabel={
                viewMode === savedView
                  ? "This view is already your default"
                  : "Set the current view as your default"
              }
              onPrismClick={saveDefaultView}
            >
              <FontAwesomeIcon icon={faThumbtack} className="projects-default-pin-icon" />
              {viewMode === savedView ? "My default view" : "Set as my default"}
            </PrismButton>
          </div>
          {showFilterBar && (
            <div className="project-fair-controls-row">
              <FilterBar
                availableYears={availableYears}
                availableTags={availableTags}
                availableLocations={availableLocations}
                selectedYears={selectedYears}
                selectedTags={selectedTags}
                selectedLocations={selectedLocations}
                search={search}
                resultCount={filtered.length}
                totalCount={projects.length}
                onYearsChange={setSelectedYears}
                onTagsChange={setSelectedTags}
                onLocationsChange={setSelectedLocations}
                onSearchChange={setSearch}
                onClear={() => {
                  setSelectedYears([]);
                  setSelectedTags([]);
                  setSelectedLocations([]);
                  setSearch("");
                }}
                trailing={<SortControl value={sort} onChange={setSort} />}
              />
            </div>
          )}
        </div>

        {loading ? (
          <PrismLoading />
        ) : crossFilterPending ? (
          <div className="projects-crossfilter-loading" role="status" aria-live="polite">
            <PrismLoading as="dots" size="lg">
              Loading projects…
            </PrismLoading>
          </div>
        ) : (
          <Suspense fallback={<PrismLoading />}>
            {(viewMode === "classic" || viewMode === "compact") &&
              cardList(viewMode === "compact")}
            {viewMode === "tagcloud" && (
              <TagCloudView
                projects={projects}
                onSelectTag={handleTagCrossFilter}
                onSelectTags={handleTagsCrossFilter}
              />
            )}
            {viewMode === "timeline" && (
              <TimelineView
                projects={projects}
                editable={isOfficer}
                onSelectYear={handleYearCrossFilter}
              />
            )}
          </Suspense>
        )}
      </main>
    </>
  );
};

export default Projects;
